/* eslint-disable react/display-name */
import React, { useMemo, useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import CustomLoader from '../components/Loader/CustomLoader'
// core components
import Header from '../components/Headers/Header'
import {
  restaurants,
  deleteRestaurant,
  toggleRestaurantAvailablity
} from '../apollo'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import SearchBar from '../components/TableHeader/SearchBar'
import {
  Container,
  Box,
  useTheme,
  Snackbar,
  Avatar,
  Typography
} from '@mui/material'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { RowActionMenu } from '../components/UI/RowActionMenu'
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined'
import { customStyles } from '../utils/tableCustomStyles'
import useGlobalStyles from '../utils/globalStyles'
import TableHeader from '../components/TableHeader'
import { Switch, Chip, Tooltip } from '@mui/material'

const GET_RESTAURANTS = gql`
  ${restaurants}
`
const DELETE_RESTAURANT = gql`
  ${deleteRestaurant}
`
const TOGGLE_AVAILABILITY = gql`
  ${toggleRestaurantAvailablity}
`

// ⋮ menu for a store row: open it, or enable / disable it on the marketplace.
const RowActions = ({ row, t, busy, onView, onToggle }) => (
  <RowActionMenu
    label={t('More actions')}
    actions={[
      {
        label: t('View store'),
        icon: StorefrontOutlinedIcon,
        tone: 'blue',
        onClick: onView
      },
      {
        label: row.isActive ? t('HideStore') : t('ShowStore'),
        icon: row.isActive ? VisibilityOffOutlined : VisibilityOutlined,
        tone: row.isActive ? 'danger' : 'green',
        divider: true,
        disabled: busy,
        onClick: onToggle
      }
    ]}
  />
)

// Hidden (isActive = false) stores are left out of every customer listing in
// the app and on the web; the vendor can still sign in and edit them.
const VisibilityCell = ({ row, t, busy, onToggle }) => (
  <Tooltip title={row.isActive ? t('HideStoreHint') : t('ShowStoreHint')} arrow>
    <Chip
      size="small"
      disabled={busy}
      icon={
        row.isActive ? (
          <VisibilityOutlined fontSize="small" />
        ) : (
          <VisibilityOffOutlined fontSize="small" />
        )
      }
      label={row.isActive ? t('Visible') : t('Hidden')}
      color={row.isActive ? 'success' : 'default'}
      variant={row.isActive ? 'outlined' : 'filled'}
      onClick={e => {
        e.stopPropagation()
        onToggle()
      }}
      sx={{ fontWeight: 600 }}
    />
  </Tooltip>
)

// Open / closed for orders. Kept in local state so the switch moves as soon as
// the mutation answers; it used to write to a plain variable and never redraw.
const OpenSwitch = ({ row, t }) => {
  const [open, setOpen] = useState(Boolean(row.isAvailable))
  const [updateAvailability, { loading }] = useMutation(TOGGLE_AVAILABILITY, {
    onCompleted: response =>
      setOpen(Boolean(response.toggleRestaurantAvailablity.isAvailable))
  })
  return (
    <Box
      onClick={e => e.stopPropagation()}
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Switch
        size="small"
        disabled={loading}
        onChange={() => updateAvailability({ variables: { id: row._id } })}
        checked={open}
      />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          color: open ? 'text.primary' : 'text.disabled',
          mt: -0.5
        }}>
        {open ? t('ON') : t('OFF')}
      </Typography>
    </Box>
  )
}

const Restaurants = props => {
  const { t } = props
  const [searchQuery, setSearchQuery] = useState('')

  const [error, setError] = useState(null)
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const globalClasses = useGlobalStyles()

  const [mutate, { loading }] = useMutation(DELETE_RESTAURANT, {
    onError: error => {
      setError(error.graphQLErrors[0].message || 'Something went wrong')
    }
  })
  const toggleVisibility = row => {
    if (
      row.isActive &&
      !window.confirm(t('ConfirmHideStore', { name: row.name }))
    )
      return
    mutate({ variables: { id: row._id } })
  }
  const {
    data,
    loading: loadingQuery,
    refetch,
    networkStatus
  } = useQuery(GET_RESTAURANTS, { fetchPolicy: 'network-only' })
  const onClickRefetch = cb => {
    cb()
  }
  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (row[field]) {
        return row[field].toLowerCase()
      }
      return row[field]
    }
    return orderBy(rows, handleField, direction)
  }

  const theme = useTheme()
  const openStore = row => {
    localStorage.setItem('restaurantId', row._id)
    localStorage.setItem('restaurantImage', row.image)
    localStorage.setItem('restaurantName', row.name)
    props.history.push(`/admin/dashboard/${row.slug}`)
  }

  const clamp2 = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  }

  const columns = [
    {
      name: t('Store'),
      selector: 'name',
      sortable: true,
      grow: 3,
      minWidth: '240px',
      cell: row => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Avatar
            variant="rounded"
            src={row.image || undefined}
            alt={row.name}
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: '#F1F3F7',
              color: 'text.disabled'
            }}>
            <StorefrontOutlinedIcon fontSize="small" />
          </Avatar>
          <Typography sx={{ ...clamp2, fontSize: 14, fontWeight: 600 }}>
            {row.name}
          </Typography>
        </Box>
      )
    },
    {
      name: t('Address'),
      selector: 'address',
      grow: 2,
      minWidth: '200px',
      cell: row => (
        <Typography
          title={row.address}
          sx={{ ...clamp2, fontSize: 13, color: 'text.secondary' }}>
          {row.address}
        </Typography>
      )
    },
    {
      name: t('Order prefix'),
      selector: 'orderPrefix',
      width: '130px',
      cell: row =>
        row.orderPrefix ? (
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: '6px',
              bgcolor: '#F1F3F7',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.04em'
            }}>
            {row.orderPrefix}
          </Box>
        ) : (
          '—'
        )
    },
    {
      name: t('Vendor'),
      selector: 'owner',
      grow: 2,
      minWidth: '220px',
      cell: row => (
        <Typography
          title={row.owner?.email}
          sx={{ ...clamp2, fontSize: 13, color: 'text.secondary' }}>
          {row.owner ? row.owner.email : '—'}
        </Typography>
      )
    },
    {
      name: t('Visibility'),
      width: '130px',
      center: true,
      sortable: true,
      selector: 'isActive',
      cell: row => (
        <VisibilityCell
          row={row}
          t={t}
          busy={loading}
          onToggle={() => toggleVisibility(row)}
        />
      )
    },
    {
      name: t('Open'),
      width: '100px',
      center: true,
      cell: row => <OpenSwitch row={row} t={t} />
    },
    {
      name: '',
      width: '56px',
      cell: row => (
        <RowActions
          row={row}
          t={t}
          busy={loading}
          onView={() => openStore(row)}
          onToggle={() => toggleVisibility(row)}
        />
      )
    }
  ]

  const conditionalRowStyles = [
    {
      when: row => !row.isActive,
      style: {
        backgroundColor: theme.palette.background.primary,
        opacity: 0.6
      }
    }
  ]

  const regex = useMemo(
    () =>
      searchQuery.length > 2
        ? new RegExp(searchQuery.toLowerCase(), 'g')
        : null,
    [searchQuery]
  )

  const filtered = useMemo(
    () =>
      searchQuery.length < 3
        ? data && data.restaurants
        : data &&
          data.restaurants.filter(restaurant => {
            return (
              (restaurant.name &&
                restaurant.name.toLowerCase().search(regex) > -1) ||
              (restaurant.orderPrefix &&
                restaurant.orderPrefix.toLowerCase().search(regex) > -1) ||
              (restaurant.owner &&
                restaurant.owner.email.toLowerCase().search(regex) > -1) ||
              (restaurant.address &&
                restaurant.address.toLowerCase().search(regex) > -1)
            )
          }),
    [searchQuery, data, regex]
  )

  return (
    <>
      <Header />
      <Container className={globalClasses.flex} fluid>
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            customStyles={customStyles}
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                placeholder={t('Search stores...')}
                value={searchQuery}
                onChange={onChangeSearch}
                onClick={() => onClickRefetch(refetch)}
              />
            }
            title={<TableHeader title={t('All stores')} count={null} />}
            headRowColor="#FFF3EB"
            columns={columns}
            data={filtered}
            pagination
            progressPending={loading || networkStatus === 4}
            progressComponent={<CustomLoader />}
            sortFunction={customSort}
            defaultSortField="name"
            onRowClicked={openStore}
            pointerOnHover
            highlightOnHover
            conditionalRowStyles={conditionalRowStyles}
          />
        )}
        <Snackbar
          open={error}
          autoHideDuration={5000}
          onClose={() => setError(null)}
          message={error}
        />
      </Container>
    </>
  )
}
export default withTranslation()(Restaurants)

// const RestStatus = ({ isAvailable, id }) => {

//   // const PinkSwitch = styled(Switch)(({ theme }) => ({
//   //   '& .MuiSwitch-switchBase.Mui-checked': {
//   //   color: '#F47627',
//   // },
//   // '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
//   //   backgroundColor: '#F6985D',
//   // },
//   // '& .MuiSwitch-switchBase': {
//   //   color: '#F47627',
//   // },
//   // '& .MuiSwitch-track': {
//   //   backgroundColor: '#ccc',
//   // },
//   // }));

//   const [error, setError] = useState(null)
//   const [checkActive, setCheckActive] = useState(isAvailable)
//   const [updateAvailability, { loading }] = useMutation(TOGGLE_AVAILABILITY, {
//     onError: error => {
//       setError(error.graphQLErrors[0].message || 'Something went wrong')
//     },
//     onCompleted: response => {
//       console.log('repsones  is = ', response.toggleRestaurantAvailablity)
//     }
//   })

//   const handleActive = () => {
//     console.log('here')
//     updateAvailability({ variables: { id } })
//     setCheckActive(!checkActive)
//   }

//   return (
//     <div>
//       {error ? (
//         <>{error}</>
//       ) : (
//         <Switch
//           size="small"
//           onChange={handleActive}
//           checked={checkActive}></Switch>
//       )}
//     </div>
//   )
// }
